<script setup lang="ts">
import { computed, reactive, ref, watch, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import CImageUploadRow from '@/components/shared/CImageUploadRow';
import type { CSelectItem } from '@/components/shared/CSelect';
import FontSelector from '../../../FontSelector.vue';
import ColorSelector from '../../../ColorSelector.vue';
import { useClickOutside } from '@/composables/clickOutside';
import { useToaster } from '@/composables/toaster';
import { useFonts } from '@/composables/fonts';
import { getCrosswordTileUploadSignedUrl } from '@/api/crossword';
import { uploadFileToS3 } from '@/utils/s3Utils';
import { detectFileType, isValidFileSignature } from '@/components/shared/CFileInput/utils/fileSignatureValidation';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import type { ColorObject } from '@/typings/libraries';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import { DEFAULT_STYLE_LETTERS } from '../../core/presets';
import type { CrosswordStyle, CrosswordStyleDraft } from '../../types';

const MIN_FONT_SIZE = 4;
const MAX_FONT_SIZE = 400;
const TILE_IMAGE_ACCEPT = '.png,.jpg,.jpeg,.webp';
const TILE_IMAGE_MAX_MB = 10;

const props = defineProps<{
    value: boolean;
    defaultName?: string;
    /** Style being edited; when absent the modal creates a new one */
    styleToEdit?: CrosswordStyle | null;
}>();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'save', value: CrosswordStyleDraft): void;
    (event: 'back'): void;
}>();

const toaster = useToaster();
const { userFonts, generateFontName, getFontFromPath } = useFonts();

const tileFiles = ref<File[]>([]);

const model = reactive({
    name: '',
    font: undefined as CSelectItem<string> | undefined,
    fontSize: DEFAULT_STYLE_LETTERS.fontSize,
    color: { hex: DEFAULT_STYLE_LETTERS.fontColor, alpha: 1 } as ColorObject,
    tileImage: null as string | null
});

const tileImageName = ref('');

const uploading = ref(false);
const pickerRef: Ref<HTMLElement | undefined> = ref();

const { clickOutsideActive } = useClickOutside(
    pickerRef,
    () => {
        clickOutsideActive.value = false;
    },
    false
);

const fontSizeIsValid = computed(() => model.fontSize >= MIN_FONT_SIZE && model.fontSize <= MAX_FONT_SIZE);

// A custom style is only usable with its tile, name, font and a size the generator accepts
const canCreate = computed(
    () => !!model.name.trim() && !!model.tileImage && !!model.font?.value && fontSizeIsValid.value && !uploading.value
);
const tileImageUrl = computed(() => (model.tileImage ? buildCloudFrontUrl(model.tileImage) : ''));

const toggleColorPicker = () => {
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onColorSelected = (color: FontColorOption) => {
    model.color = color as ColorObject;
    toggleColorPicker();
};

const onTileImageUpload = async (files: File[]) => {
    const [file] = files;
    if (!file) return;

    uploading.value = true;
    try {
        if (!(await isValidFileSignature(file, TILE_IMAGE_ACCEPT))) {
            throw new Error(`This file is not a valid image (detected: ${(await detectFileType(file)) ?? 'unknown'})`);
        }

        const signedUrl = await getCrosswordTileUploadSignedUrl(file.name);
        await uploadFileToS3(signedUrl.SignedUrl, signedUrl.Mime, file, signedUrl.CacheControl);
        model.tileImage = signedUrl.Path;
        // The stored file is a guid, so the row keeps showing the name the user picked
        tileImageName.value = file.name;
    } catch (error) {
        console.error(error);
        toaster.error('We could not upload the tile image, please try again.');
    } finally {
        // The uploaded path is the source of truth, keeping the file would flip CFileInput to preview mode
        tileFiles.value = [];
        uploading.value = false;
    }
};

const removeTileImage = () => {
    model.tileImage = null;
    tileImageName.value = '';
    tileFiles.value = [];
};

const getFileName = (path: string) => path.split('/').pop() || '';

const fontItem = (path: string): CSelectItem<string> | undefined => {
    const font = getFontFromPath(path);
    return font ? { label: generateFontName(font), value: font.Path } : undefined;
};

const defaultFontItem = (): CSelectItem<string> | undefined =>
    fontItem(DEFAULT_STYLE_LETTERS.font) ||
    (() => {
        const arial = userFonts.value.find((userFont) => userFont.Name.toLowerCase() === 'arial');
        return arial ? { label: generateFontName(arial), value: arial.Path } : undefined;
    })();

const isEditing = computed(() => !!props.styleToEdit);

const reset = () => {
    const style = props.styleToEdit;

    model.name = style?.name ?? props.defaultName ?? '';
    model.font = style?.font ? fontItem(style.font) : defaultFontItem();
    model.fontSize = style?.fontSize ?? DEFAULT_STYLE_LETTERS.fontSize;
    model.color = { hex: style?.fontColor ?? DEFAULT_STYLE_LETTERS.fontColor, alpha: 1 } as ColorObject;
    model.tileImage = style?.tileImage ?? null;
    tileImageName.value = style?.tileImage ? getFileName(style.tileImage) : '';
    tileFiles.value = [];
    clickOutsideActive.value = false;
};

const handleSave = () => {
    if (!canCreate.value) return;

    emit('save', {
        name: model.name.trim(),
        custom: true,
        tileImage: model.tileImage,
        // A custom style is previewed by its own tile, there is no catalog thumbnail for it
        thumbnail: model.tileImage || undefined,
        font: model.font!.value,
        fontSize: model.fontSize,
        fontColor: model.color.hex
    });
};

const handleBack = () => {
    emit('back');
};

watch(
    () => props.value,
    (isOpen) => {
        if (isOpen) reset();
    },
    { immediate: true }
);

// The fonts may still be loading when the modal opens
watch(userFonts, () => {
    if (model.font) return;
    model.font = props.styleToEdit?.font ? fontItem(props.styleToEdit.font) : defaultFontItem();
});
</script>

<template>
    <c-modal
        :value="value"
        :title="isEditing ? 'Edit your Crossword Style' : 'Set Up your Custom Crossword Style'"
        size="lg"
        hide-footer
        @input="emit('input', $event)"
        @closed="emit('input', false)"
    >
        <c-input v-model="model.name" label="Style Name" type="text" :debounce="0" placeholder="Custom Style" />

        <section class="section mt-3">
            <header class="section-header">Letters</header>
            <div class="section-body">
                <font-selector v-model="model.font" />
                <!-- No min/max here: CInput clamps on every keystroke, so typing "10" would jump to the minimum -->
                <c-input
                    v-model="model.fontSize"
                    type="decimal"
                    step=".01"
                    label="Font size (px)"
                    placeholder="0.00"
                    :error="!fontSizeIsValid"
                    :error-msg="`Use a size between ${MIN_FONT_SIZE} and ${MAX_FONT_SIZE} px`"
                />
                <div class="color-field">
                    <label>Color</label>
                    <div class="framed-row" @click="toggleColorPicker">
                        <span class="color-preview" :style="{ backgroundColor: model.color.hex }" />
                        {{ model.color.hex }}
                    </div>
                    <color-selector
                        ref="pickerRef"
                        :open="clickOutsideActive"
                        :color="model.color"
                        @cancel="toggleColorPicker"
                        @color-selected="onColorSelected"
                    />
                </div>
            </div>
        </section>

        <section class="section mt-3">
            <header class="section-header">Tile Image</header>
            <div class="section-body">
                <label>Tile Image</label>
                <c-image-upload-row
                    :value="tileFiles"
                    :image-url="tileImageUrl"
                    :name="tileImageName"
                    :accept="TILE_IMAGE_ACCEPT"
                    :max-mb-size="TILE_IMAGE_MAX_MB"
                    :disabled="uploading"
                    :removable="!!model.tileImage"
                    @input="onTileImageUpload"
                    @remove="removeTileImage"
                />
            </div>
        </section>

        <div class="d-flex justify-content-between custom-footer">
            <c-button :text="isEditing ? 'Cancel' : 'Back'" variant="secondary" @click="handleBack" />
            <c-button
                :text="isEditing ? 'Save Style' : 'Create Style'"
                variant="primary"
                :disabled="!canCreate"
                @click="handleSave"
            />
        </div>
    </c-modal>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.section {
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;

    .section-header {
        background-color: get-color('gray-lightest');
        border-bottom: 1px solid get-color('gray-light');
        padding: 0.75rem 1rem;
        font-weight: 600;
    }

    .section-body {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;

        label {
            margin-bottom: 0.25rem;
        }
    }
}

.framed-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;
    padding: 0.625rem;
    cursor: pointer;

    .color-preview {
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 0.125rem;
    }
}

.custom-footer {
    border-top: 1px solid get-color('gray-light');
    margin: 1rem -1rem 0;
    padding: 1rem 1rem 0;
}
</style>
